'use client';

import { useTranslations } from 'next-intl';
import DocumentPageShell from '@/components/sections/shared/DocumentPageShell';
import PdfDocumentCard, { DocumentGrid } from '@/components/ui/PdfDocumentCard';
import { resolveStrapiMediaUrl } from '@/lib/strapi-media-url';
import type { DownloadData } from '@/types/download';

interface DownloadsProps {
  locale: string;
  data: DownloadData | null;
}

export default function Downloads({ data }: DownloadsProps) {
  const t = useTranslations('downloads');
  const items = data?.item ?? [];

  if (!data) {
    return (
      <DocumentPageShell title={t('error.title')} subtitle={<p>{t('error.message')}</p>}>
        <div className="rounded-2xl border border-[#e4e4e4] bg-white p-8 text-center shadow-sm">
          <p className="text-base text-[#737373]">{t('error.message')}</p>
        </div>
      </DocumentPageShell>
    );
  }

  return (
    <DocumentPageShell
      eyebrow={t('eyebrow')}
      title={data.pageTitle}
      subtitle={<p>{t('subtitle')}</p>}
    >
      <div className="overflow-hidden rounded-2xl border border-[#e4e4e4] bg-white p-5 shadow-sm sm:p-6 md:p-8">
        <div className="mb-6 flex flex-col gap-2 border-b border-[#f0f0f0] pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="font-figtree text-[10px] font-bold uppercase tracking-[0.2em] text-[#A09A93]">
              {t('sectionLabel')}
            </p>
            <h2 className="mt-1 text-lg font-semibold tracking-tight text-[#1C1917] sm:text-xl">
              {t('sectionTitle')}
            </h2>
          </div>
          <p className="text-sm text-[#737373]">{t('documentCount', { count: items.length })}</p>
        </div>

        {items.length > 0 ? (
          <DocumentGrid>
            {items.map((item) => (
              <PdfDocumentCard
                key={item.id}
                item={{
                  id: item.id,
                  title: item.title,
                  href: resolveStrapiMediaUrl(item.uploadPdf?.url) ?? null,
                }}
              />
            ))}
          </DocumentGrid>
        ) : (
          <p className="rounded-xl bg-[#faf9f7] px-4 py-6 text-center text-sm text-[#737373]">
            No documents available.
          </p>
        )}
      </div>
    </DocumentPageShell>
  );
}
